/* eslint-disable */
import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Landmark } from 'lucide-react'
import { Refresh, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useRecoilRefresher_UNSTABLE, useRecoilValueLoadable } from 'recoil'
import { GetAllMandates } from '../../store/MFBasketsStore'
import { Button } from '../../Components/ui/button'
import { toCurrency } from '../../utils/CurrencyUtil'
import { capitalizeWords, formatDate } from '../../utils/Util'
import MFOrdersSkeleton from '../../Components/SkeletonLoaders/MFOrdersSkeleton'
import SIPsEmpty from '../../Components/EmptyState/SIPsEmpty'
import env from '../../env/env'

const parseApiDate = (ts) => {
    if (!ts) return 0
    const normalized = ts.replace(' ', 'T')
    const d = Date.parse(normalized)
    return Number.isNaN(d) ? 0 : d
}

const MANDATE_STATUS_META = {
    NEW: { label: 'Client Approval Awaited', className: 'text-error-600' },
    REQUESTED: { label: 'Client Approval Awaited', className: 'text-error-600' },
    'WAITING FOR CLIENT AUTHENTICATION': {
        label: 'Client Approval Awaited',
        className: 'text-error-600',
    },
    'RECEIVED BY EXCHANGE': { label: 'In Progress', className: 'text-warning-600' },
    APPROVED: { label: 'Approved', className: 'text-secondary-600' },
    ACTIVE: { label: 'Active', className: 'text-secondary-600' },
    FAILED: { label: 'Failed', className: 'text-error-600' },
    REJECTED: { label: 'Rejected', className: 'text-error-600' },
    CANCELLED: { label: 'Cancelled', className: 'text-error-600' },
}

const getStatusMeta = (rawStatus) => {
    const normalized = String(rawStatus || '')
        .trim()
        .toUpperCase()
    const mappedStatus = MANDATE_STATUS_META[normalized]

    if (mappedStatus) return mappedStatus

    return {
        label: capitalizeWords(String(rawStatus || '—').toLowerCase()),
        className: 'text-black-400',
    }
}

const displayStatus = (raw) => {
    return getStatusMeta(raw).label
}

const mandateStatusClass = (status) => {
    return getStatusMeta(status).className
}

const ShowAllMandate = () => {
    const mandatesLoadable = useRecoilValueLoadable(GetAllMandates)
    const refreshMandates = useRecoilRefresher_UNSTABLE(GetAllMandates)
    const [expandedId, setExpandedId] = useState(null)

    const getBankLogo = (ifscCode) => {
        const extractedBranch = ifscCode?.match(/^[A-Za-z]{4}/)[0]?.toLowerCase()
        const iconSrc = `${env.BANK_LOGOS}/${extractedBranch}/symbol.svg`
        return iconSrc
    }

    useEffect(() => {
        refreshMandates()
    }, [refreshMandates])

    const rows = useMemo(() => {
        const { state, contents } = mandatesLoadable
        if (state !== 'hasValue' || !contents) return []
        if (Array.isArray(contents)) return []
        const list = contents?.data
        if (!Array.isArray(list)) return []
        return [...list].sort(
            (a, b) => parseApiDate(b?.updatedTimeStamp) - parseApiDate(a?.updatedTimeStamp),
        )
    }, [mandatesLoadable])

    const isFetchFailed =
        mandatesLoadable.state === 'hasValue' && Array.isArray(mandatesLoadable.contents)

    switch (mandatesLoadable.state) {
        case 'loading':
            return <MFOrdersSkeleton />
        case 'hasError':
            return (
                <div className='max-w-[1128px] mx-auto py-10 px-4 text-center'>
                    <p className='text-error-600 font-medium mb-4'>Something went wrong while loading mandates.</p>
                    <Button type='button' variant='outline' onClick={() => refreshMandates()}>
                        Try again
                    </Button>
                </div>
            )
        default:
            break
    }

    return (
        <div className='py-6'>
            <div className='max-w-[1128px] mx-auto px-2 lg:px-0'>
                <div className='flex items-center justify-between gap-4 py-4'>
                    <h2 className='font-semibold text-black-500 leading-6 tracking-[-4%] flex items-center gap-x-1'>
                        My mandates
                        <ChevronRight className='h-5 w-5 opacity-60' />
                    </h2>
                    <Button
                        type='button'
                        size='simple'
                        variant='ghost'
                        className='gap-2 text-primary-500'
                        onClick={() => refreshMandates()}
                    >
                        <Refresh sx={{ fontSize: 22 }} />
                        <span className='hidden sm:inline'>Refresh</span>
                    </Button>
                </div>
            </div>

            {isFetchFailed ? (
                <div className='max-w-[1128px] mx-auto px-4 text-center py-8'>
                    <p className='text-error-600 font-medium mb-4'>Unable to load mandates.</p>
                    <Button type='button' variant='outline' onClick={() => refreshMandates()}>
                        Try again
                    </Button>
                </div>
            ) : rows.length === 0 ? (
                <div className='max-w-[1128px] mx-auto'>
                    <SIPsEmpty
                        title='No mandates yet'
                        description="When you create or register a bank mandate, it will show up here with status and limits."
                        mainText=''
                        page='/dashboard/mutual-funds'
                        showButton={false}
                        variant='mandates'
                    />
                </div>
            ) : (
                <div className='max-w-[1128px] mx-auto mt-2'>
                    <div className='w-full mx-auto overflow-hidden px-4 lg:px-0'>
                        {rows.map((m) => {
                            const id = m.id
                            const open = expandedId === id
                            const title = m.mandateId
                                ? `Mandate`
                                : `${m.mandateType || 'Universal'} Mandate`

                            return (
                                <div
                                    key={id}
                                    className='border-b border-gray-200 last:border-b-0 cursor-pointer'
                                >
                                    <div className='p-4 flex items-start'>
                                        <div className='flex justify-between w-full gap-3'>
                                            <button
                                                type='button'
                                                className='flex flex-1 text-left gap-3 min-w-0'
                                                onClick={() => setExpandedId(open ? null : id)}
                                            >
                                                <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-gray-100 text-gray-700'>
                                                    {m?.ifscCode ? <img src={getBankLogo(m.ifscCode)} alt='bank logo' className='h-6 w-6' /> : <Landmark className='h-6 w-6' />}

                                                </div>
                                                <div className='min-w-0 flex-1'>
                                                    <p className='font-semibold text-gray-900 leading-6 truncate'>{title}</p>

                                                    <p className={`text-sm font-medium mt-0.5 ${mandateStatusClass(m.mandateStatus)}`}>
                                                        {displayStatus(m.mandateStatus)}
                                                    </p>

                                                    {(m.mandateStatus.toLowerCase()) === 'requested' ? (
                                                        <p className='text-sm font-medium text-black-400 mt-0.5'>
                                                            Go to Action Plan & Approve
                                                        </p>
                                                    ) : null}

                                                    {m.createdTimeStamp && displayStatus(m.mandateStatus) !== 'Client Approval Awaited' ? (
                                                        <p className='text-sm font-medium text-black-400 mt-0.5'>
                                                            Created : {formatDate(m.createdTimeStamp)}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </button>
                                            <div className='flex flex-col items-end gap-1 shrink-0'>
                                                <p className='font-semibold leading-6 text-gray-900'>
                                                    ₹ {m.amount != null ? toCurrency(Number(m.amount)) : '—'}
                                                </p>
                                                <button
                                                    type='button'
                                                    className='text-black-200 p-0.5'
                                                    aria-label={open ? 'Collapse details' : 'Expand details'}
                                                    onClick={() => setExpandedId(open ? null : id)}
                                                >
                                                    {open ? (
                                                        <KeyboardArrowUp sx={{ fontSize: 24 }} />
                                                    ) : (
                                                        <KeyboardArrowDown sx={{ fontSize: 24 }} />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`px-4 pb-4 -mt-1 overflow-hidden transition-all duration-200 ease-out ${open ? 'max-h-[480px] opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                    >
                                        <div className='ml-0 sm:ml-[60px] space-y-2 text-sm border-t border-dashed border-gray-200 pt-3 text-black-500'>

                                            <div className='flex justify-between gap-4'>
                                                <span className='text-black-400'>Account</span>
                                                <span className='font-medium text-right break-all'>{m.accountNumber || '—'}</span>
                                            </div>
                                            <div className='flex justify-between gap-4'>
                                                <span className='text-black-400'>IFSC</span>
                                                <span className='font-medium text-right'>{m.ifscCode || '—'}</span>
                                            </div>
                                            <div className='flex justify-between gap-4'>
                                                <span className='text-black-400'>Mandate ID</span>
                                                <span className='font-medium text-right'>{m.mandateId || '—'}</span>
                                            </div>
                                            {/* <div className='flex justify-between gap-4'>
                                                <span className='text-black-400'>SIP end</span>
                                                <span className='font-medium text-right'>{m.sipEndDate || '—'}</span>
                                            </div> */}
                                            {/* <div className='flex justify-between gap-4'>
                                                <span className='text-black-400'>Created</span>
                                                <span className='font-medium text-right'>{m.createdTimeStamp || '—'}</span>
                                            </div> */}

                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ShowAllMandate
